import { useTranslation } from "react-i18next";
import { Link, useSearchParams } from "react-router-dom";

import { ApiException } from "../../lib/api";
import { getApiErrorMessage, normalizeLang } from "../../lib/apiErrorMessage";
import { useIntakeEvents } from "../../lib/queries/intakeLinks";
import { useSubmissions } from "../../lib/queries/submissions";

function ListState({ loading, error }: { loading: boolean; error: unknown }) {
  const { t, i18n } = useTranslation();
  if (error)
    return (
      <p role="alert" className="text-sm text-red-700">
        {error instanceof ApiException
          ? getApiErrorMessage(error, normalizeLang(i18n.resolvedLanguage))
          : t("errors.generic")}
      </p>
    );
  return loading ? <p role="status">{t("app.loading")}</p> : null;
}
function PageLinks({ next, fetching }: { next?: string | null; fetching: boolean }) {
  const { t } = useTranslation();
  const [params, setParams] = useSearchParams();
  return (
    <nav
      className="workspace-action-bar mt-4 flex flex-wrap gap-3"
      aria-label={t("reception_flow.receipts")}
    >
      {params.has("cursor") && (
        <button
          className="btn-secondary"
          disabled={fetching}
          onClick={() =>
            setParams((previous) => {
              const value = new URLSearchParams(previous);
              value.delete("cursor");
              return value;
            })
          }
        >
          {t("reception_flow.first_page")}
        </button>
      )}
      {next && (
        <button
          className="btn-secondary"
          disabled={fetching}
          onClick={() =>
            setParams((previous) => {
              const value = new URLSearchParams(previous);
              value.set("cursor", next);
              return value;
            })
          }
        >
          {t("reception_flow.next")}
        </button>
      )}
    </nav>
  );
}
function Documents() {
  const { t, i18n } = useTranslation();
  const [params] = useSearchParams();
  const query = useIntakeEvents({ cursor: params.get("cursor"), limit: 25 });
  return (
    <>
      <ListState loading={query.isFetching} error={query.error} />
      {!query.error && !query.isPlaceholderData && (
        <ul className="divide-y rounded-xl border bg-white">
          {query.data?.items.map((event) => (
            <li key={event.id} className="space-y-2 p-4">
              <div className="flex flex-wrap gap-2 text-sm">
                <span>
                  {t(`intake_events.detail.source_kinds.${event.source_kind}`, {
                    defaultValue: event.source_kind,
                  })}
                </span>
                <span>
                  ·{" "}
                  {t(
                    `intake_events.detail.materialize.wizard.step_1_doc_direction.options.${event.doc_direction}`,
                    {
                      defaultValue: event.doc_direction,
                    },
                  )}
                </span>
                <span>
                  ·{" "}
                  {t(`intake_events.detail.statuses.${event.status}`, {
                    defaultValue: event.status,
                  })}
                </span>
              </div>
              <div className="flex flex-wrap items-center justify-between gap-3 text-sm">
                <time dateTime={event.created_at}>
                  {new Date(event.created_at).toLocaleString(i18n.resolvedLanguage)}
                </time>
                <Link
                  className="underline"
                  to={`/intake-events/${event.id}${params.has("cursor") ? `?receipt_cursor=${encodeURIComponent(params.get("cursor")!)}` : ""}`}
                >
                  {t("reception_flow.read")}
                </Link>
              </div>
            </li>
          ))}
        </ul>
      )}
      {query.data?.items.length === 0 && !query.error && (
        <p className="py-4">{t("reception_flow.empty")}</p>
      )}
      <PageLinks next={query.error ? null : query.data?.next_cursor} fetching={query.isFetching} />
    </>
  );
}
function Offers() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const query = useSubmissions({ cursor: params.get("cursor"), limit: 25 });
  return (
    <>
      <ListState loading={query.isFetching} error={query.error} />
      {!query.error && !query.isPlaceholderData && (
        <ul className="divide-y rounded-xl border bg-white">
          {query.data?.items.map((offer) => (
            <li key={offer.id} className="flex flex-wrap items-center justify-between gap-3 p-4">
              <div>
                <p className="font-medium">{offer.insurer_name}</p>
                <p className="text-sm text-slate-600">
                  {t(`submissions.statuses.${offer.status}`)}
                </p>
              </div>
              <Link
                className="text-sm underline"
                to={`/submissions/${offer.id}?from=receipts${params.has("cursor") ? `&receipt_cursor=${encodeURIComponent(params.get("cursor")!)}` : ""}`}
              >
                {t("reception_flow.read")}
              </Link>
            </li>
          ))}
        </ul>
      )}
      {query.data?.items.length === 0 && !query.error && (
        <p className="py-4">{t("reception_flow.empty")}</p>
      )}
      <PageLinks next={query.error ? null : query.data?.next_cursor} fetching={query.isFetching} />
    </>
  );
}
/** Separate server lists, no inferred direction, no partial-page aggregation. */
export function ReceptionsPage() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const offers = params.get("tab") === "offers";
  return (
    <div className="mx-auto max-w-screen-xl">
      <header className="workspace-command-bar mb-4 space-y-3 py-3">
        <h1 className="text-xl font-semibold">{t("nav.receptions")}</h1>
        <nav className="flex flex-wrap gap-3 text-sm">
          <Link
            className="underline"
            aria-current={!offers ? "page" : undefined}
            to="/intake-events"
          >
            {t("reception_flow.receipts")}
          </Link>
          <Link
            className="underline"
            aria-current={offers ? "page" : undefined}
            to="/intake-events?tab=offers"
          >
            {t("reception_flow.offers")}
          </Link>
          <Link className="underline" to="/reponses">
            {t("reception_flow.forms")}
          </Link>
          <Link className="underline" to="/intake-links">
            {t("reception_flow.invitations")}
          </Link>
          <Link
            className="focus-visible:ring-brand-500 min-h-11 underline focus-visible:ring-2"
            to="/intake-events/email"
          >
            {t("voluntary_email.upload")}
          </Link>
          <Link className="underline" to="/intake-events/new">
            {t("workspace.upload")}
          </Link>
        </nav>
      </header>
      <p className="mb-4 text-sm text-slate-600">{t("reception_flow.lists")}</p>
      {offers ? <Offers /> : <Documents />}
    </div>
  );
}
